import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { SiteFooter } from "../components/SiteFooter";
import { PhotoGallery } from "../components/PhotoGallery";
import { IMAGES, SELF_SERVE } from "../lib/business";
import { CheckCircle2, MapPin, QrCode, Car, Sparkles, ArrowRight } from "lucide-react";


export default function Landing() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config", { params: { origin: window.location.origin } }).then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const price = cfg?.price ?? 49.95;

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={IMAGES.speedway} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-36">
          <div className="max-w-2xl reveal">
            <div className="overline text-volt mb-5 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Earlwood NSW 2206
            </div>
            <h1 className="font-head font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight uppercase">
              Unlimited Shine.<br />One Flat Membership.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl tracking-wide">
              Automatic car wash membership at 200 Homer St. Register your number plate, scan the QR on arrival, and drive away spotless.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-join-btn" className="group flex items-center gap-2 bg-volt text-white px-8 py-4 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors">
                Start Membership <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="flex items-baseline gap-1">
                <span className="font-head font-black text-4xl text-white">${price}</span>
                <span className="overline text-muted-foreground">AUD / plate / month</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="overline text-volt mb-3">How It Works</div>
        <h2 className="font-head font-extrabold text-3xl md:text-4xl uppercase tracking-tight mb-12">Three Steps To A Clean Car</h2>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { icon: Car, t: "Register Your Plate", d: `Create an account and add your number plate. ${'$'}${price} AUD per plate, per month.` },
            { icon: QrCode, t: "Scan On Arrival", d: "Scan the QR code at 200 Homer St to pull up your membership instantly." },
            { icon: Sparkles, t: "Get Your Ticket", d: "Confirm your plate, receive a wash ticket, and roll into the bay." },
          ].map((s, i) => (
            <div key={s.t} className="bg-background p-8 hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 border border-volt flex items-center justify-center mb-6">
                <s.icon className="w-6 h-6 text-volt" />
              </div>
              <div className="overline text-muted-foreground mb-2">Step {i + 1}</div>
              <h3 className="font-head font-bold text-2xl uppercase tracking-tight mb-3">{s.t}</h3>
              <p className="text-sm text-muted-foreground tracking-wide">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Membership + image */}
      <section className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <img src={IMAGES.autoWash} alt="Earlwood automatic car wash at Speedway" className="w-full h-[420px] object-cover border border-white/10" />
        </div>
        <div>
          <div className="overline text-volt mb-3">Membership</div>
          <h2 className="font-head font-extrabold text-3xl md:text-4xl uppercase tracking-tight mb-6">Everything Included</h2>
          <div className="border border-white/10">
            {[
              "Unlimited automatic washes, every day",
              "Auto-renews monthly — cancel anytime",
              "10% off every extra car you add",
              "Instant QR check-in — no queues",
              "One membership per number plate",
            ].map((f) => (
              <div key={f} className="flex items-center gap-3 p-4 border-b border-white/10 last:border-b-0">
                <CheckCircle2 className="w-5 h-5 text-volt shrink-0" />
                <span className="text-sm tracking-wide">{f}</span>
              </div>
            ))}
          </div>
          <Link to="/register" data-testid="membership-join-btn" className="mt-8 inline-flex items-center gap-2 bg-volt text-white px-8 py-4 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors">
            Join For ${price}/mo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* More services */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="overline text-volt mb-3">More At Earlwood</div>
        <h2 className="font-head font-extrabold text-3xl md:text-4xl uppercase tracking-tight mb-10">Self-Serve Bays &amp; Dog Wash</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { to: "/self-serve", img: IMAGES.selfServe, t: "Self-Serve Bays", d: `From ${SELF_SERVE.from} for ${SELF_SERVE.per} · Open ${SELF_SERVE.hours}`, id: "landing-self-serve-card" },
            { to: "/dog-wash", img: IMAGES.dogWash, t: "Dog Wash", d: "$10 for 10 min · Top up anytime · Open 24/7", id: "landing-dog-wash-card" },
          ].map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.id} className="group relative block h-72 overflow-hidden border border-white/10">
              <img src={c.img} alt={c.t} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="font-head font-bold text-2xl uppercase tracking-tight">{c.t}</h3>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">{c.d} <ArrowRight className="w-4 h-4 text-volt group-hover:translate-x-1 transition-transform" /></p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <PhotoGallery />

      {/* Scan to join */}
      {cfg?.join_qr_base64 && (
        <section className="border-t border-white/10">
          <div className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="overline text-volt mb-3 flex items-center gap-2"><QrCode className="w-4 h-4" /> Scan To Join</div>
              <h2 className="font-head font-extrabold text-3xl md:text-4xl uppercase tracking-tight mb-4">Point Your Camera, Become A Member</h2>
              <p className="text-base text-muted-foreground max-w-md tracking-wide mb-6">
                Scan the code to open sign-up on your phone — register your number plate and start your ${price} AUD/month membership in minutes.
              </p>
              <Link to="/register" data-testid="scan-join-btn" className="inline-flex items-center gap-2 bg-volt text-white px-8 py-4 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors">
                Or Join Here <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex lg:justify-end">
              <div className="border border-white/10 bg-white p-6" data-testid="landing-join-qr">
                <img src={cfg.join_qr_base64} alt="Scan to join Earlwood Automatic Car Wash" className="w-56 h-56" />
              </div>
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
